import { useMemo } from 'react';
import './DurationSparkline.css';

/**
 * Inline duration history for one procedure.
 *
 * A single `last_run_duration_ms` cannot distinguish "this procedure takes
 * fifteen minutes" from "this procedure usually takes two seconds and
 * today took fifteen minutes". Those are different facts and only one of
 * them is a problem.
 *
 * Hand-rolled SVG rather than a charting library: this is a table cell,
 * not a 180px chart with axes, and eight instances of a general-purpose
 * chart is a lot of bundle for a shape that is four lines of arithmetic.
 *
 * The **spread** is the number worth reading, not the shape. Measured
 * across seven days on the predecessor deployment:
 *
 *     pulse.collector.github   median 910.5s   max 1144.1s    1.3x
 *     pulse.collector.jira     median   1.6s   max   98.2s   60.6x
 *
 * The github collector is slow and *predictable*; the jira collector is
 * fast and *erratic*, occasionally taking sixty times its usual. Only the
 * second is worth investigating, and a chart alone does not say which is
 * which — so the ratio is rendered as text and highlighted once it passes
 * the point where variance stops being noise.
 */

/** Above this, max-vs-median stops looking like ordinary jitter. */
const SPREAD_NOTABLE = 5;

export function formatMs(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.round((ms % 60_000) / 1000);
  return s === 0 ? `${m}m` : `${m}m${s}s`;
}

interface Props {
  /** Durations in milliseconds, oldest first. */
  durations: number[];
  width?: number;
  height?: number;
}

export function DurationSparkline({
  durations,
  width = 68,
  height = 16,
}: Props) {
  const stats = useMemo(() => {
    const sorted = [...durations].sort((a, b) => a - b);
    // Destructured rather than indexed. `noUncheckedIndexedAccess` types
    // `sorted[i]` as `number | undefined` even after a length check, and
    // silencing that with `!` would also silence a real off-by-one.
    const [min] = sorted;
    const median = sorted[Math.floor(sorted.length / 2)];
    const max = sorted[sorted.length - 1];
    if (min === undefined || median === undefined || max === undefined) {
      return null;
    }
    // Guard the divide: a procedure whose median rounds to 0ms —
    // `support.intake.quality` finishes in 20ms and does exactly this —
    // would otherwise report an infinite spread.
    const spread = median > 0 ? max / median : 1;
    return { median, max, min, spread };
  }, [durations]);

  if (!stats) return <span className="dur-spark__none">—</span>;

  // A single run has no shape to draw; the number is the whole story.
  const points =
    durations.length < 2
      ? null
      : durations
          .map((d, i) => {
            const x = (i / (durations.length - 1)) * width;
            // Scale against max, floored at 1 so a flat series of zeros
            // renders along the baseline rather than dividing by zero.
            const y = height - (d / Math.max(stats.max, 1)) * (height - 2) - 1;
            return `${x.toFixed(1)},${y.toFixed(1)}`;
          })
          .join(' ');

  const notable = stats.spread >= SPREAD_NOTABLE;

  return (
    <span
      className="dur-spark"
      title={`${durations.length} run${durations.length === 1 ? '' : 's'} · median ${formatMs(stats.median)} · range ${formatMs(stats.min)}–${formatMs(stats.max)}`}
    >
      {points && (
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          className={`dur-spark__svg${notable ? ' is-notable' : ''}`}
          aria-hidden="true"
        >
          <polyline
            points={points}
            fill="none"
            strokeWidth="1.2"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </svg>
      )}
      <span className="dur-spark__value">
        {formatMs(stats.median)}
        {notable && (
          <span className="dur-spark__spread">
            ×
            {stats.spread < 10
              ? stats.spread.toFixed(1)
              : Math.round(stats.spread)}
          </span>
        )}
      </span>
    </span>
  );
}
